/**
 * Content data for SEO landing pages — service pages, location pages, blog posts.
 * Each entry produces a dedicated /services/:slug, /service-areas/:slug, /blog/:slug page.
 * Copy is optimized for local Halifax / HRM Nova Scotia keywords.
 */

// -----------------------------------------------------------------------------
// SERVICE PAGES  (maps by SERVICES.id from /data/services.js)
// -----------------------------------------------------------------------------
export const SERVICE_PAGE_CONTENT = {
  "wash-wax": {
    slug: "wash-wax",
    priceFrom: 150,
    metaTitle: "Mobile Car Wash & Wax in Halifax & Dartmouth NS",
    metaDescription:
      "Premium mobile wash & wax detailing in Halifax, Dartmouth and across HRM. Foam cannon, microfiber wash, DI water rinse and choice of spray or carnauba wax — starting at $150. Booked online, done in your driveway.",
    keywords:
      "car wash halifax, mobile car wash dartmouth, wash and wax hrm, foam cannon detailing nova scotia, mobile detailing bedford",
    hero: {
      eyebrow: "Wash & Wax",
      title: "Wet-look shine, brought to your driveway.",
      sub: "The proper hand-wash your car has been missing. Foam-cannon rinse, microfiber pad scrub, wheels & tires cleaned, DI water rinse to prevent spots, and your choice of spray or carnauba wax.",
    },
    sections: [
      {
        h: "What's included in our Halifax mobile wash & wax",
        p: "Every Wash & Wax booking includes a full pre-rinse to knock loose contaminants off the paint, a foam cannon application with pH-balanced soap, a two-bucket microfiber pad scrub done entirely by hand, dedicated wheel & tire cleaning, and a deionized (DI) water rinse to eliminate water spots. You choose between our long-lasting spray wax or a hand-applied carnauba hard wax for a deeper wet-look finish.",
      },
      {
        h: "Why hand washing beats the drive-through — especially in HRM",
        p: "Nova Scotia's mix of Atlantic salt spray, harbour fog, tree sap along Waverley Road, and winter road brine is brutal on clear coat. Automatic car washes drag those contaminants across your paint with recycled brushes, creating swirl marks and micro-scratches that dull your finish over time. Our hand method — combined with clean microfiber mitts flipped or replaced constantly — protects your paint while getting it dramatically cleaner.",
      },
      {
        h: "Fully mobile — Halifax, Dartmouth, Bedford, Sackville and beyond",
        p: "We bring our own water supply, generator power, and pressure system directly to your home or business anywhere in HRM. No need to schedule around a bay, no drive time on your end. Book online, we show up on time, and you get your afternoon back.",
      },
    ],
    faqs: [
      {
        q: "How long does a mobile wash & wax take in Halifax?",
        a: "Roughly 1 hour 30 minutes for a standard sedan. Larger SUVs and trucks add 15–30 minutes.",
      },
      {
        q: "Do I need to provide water or power?",
        a: "No — our mobile unit carries its own water tank and generator. We just need approximately 15 feet of level driveway or parking space.",
      },
      {
        q: "How often should I get a wash & wax in Nova Scotia?",
        a: "We recommend every 4–6 weeks during our April–November season, especially after coastal storms or pollen surges.",
      },
      {
        q: "Can you remove tree sap or road tar?",
        a: "Yes — tree sap removal is an available add-on. We use dedicated sap/tar remover before the wax step so it doesn't get sealed in.",
      },
    ],
  },
  "express-interior": {
    slug: "express-interior-detail",
    priceFrom: 150,
    metaTitle: "Express Interior Detailing in Halifax, Dartmouth & HRM",
    metaDescription:
      "Same-day express interior detail in Halifax Regional Municipality. Full vacuum, dash & trim clean, interior glass, pet hair removal available. Starting at $150. We come to you.",
    keywords:
      "interior detailing halifax, car vacuum service dartmouth, interior car cleaning bedford, mobile interior detail hrm, pet hair removal nova scotia",
    hero: {
      eyebrow: "Express Interior Detail",
      title: "The quick reset your interior needs.",
      sub: "Full vacuum, dashboard & trim detailed, interior glass cleaned, doors & jambs wiped, plus shine applied to dash and doors. Two hours of proper hand work.",
    },
    sections: [
      {
        h: "Perfect for busy Halifax drivers",
        p: "If your daily commute across the MacKay Bridge or up Bedford Highway has left your cabin covered in Timmies dust, road salt, kid crumbs and dog hair — this is the fastest way to get it factory-fresh again. Two hours, mobile, done in your driveway before you get home from work.",
      },
      {
        h: "What we clean, precisely",
        p: "Every square inch of the dash, vinyl, plastic trims, doors, door jambs, cupholders and console — hand-detailed with proper interior cleaner and microfiber. All carpets, seats, floor mats and the trunk are fully vacuumed with attachments to reach under seats and between the rails. Interior glass is finished with streak-free glass cleaner, and a final shine dressing is applied to the dash and door panels.",
      },
      {
        h: "Popular add-ons",
        p: "Pet hair removal is our most-requested add-on across HRM — from Labs in Cole Harbour to Huskies in Fall River. We also offer full odour elimination (bomb or spray), and pressure-washed rubber mats for anyone dealing with winter salt residue.",
      },
    ],
    faqs: [
      {
        q: "Does the Express Interior include shampooing?",
        a: "No — for shampoo/steam extraction of seats and carpets, you want our Premium Interior + Shampooing or the full Darkside Detail.",
      },
      {
        q: "Can you remove pet hair from my Halifax vehicle?",
        a: "Yes. Pet hair removal is an available add-on ($30 sedan / $40 SUV / $50 larger SUV) and gets deeply embedded hair out of upholstery.",
      },
      {
        q: "How long does the Express Interior take?",
        a: "About 2 hours on a standard sedan.",
      },
    ],
  },
  "premium-interior-exterior": {
    slug: "premium-interior-exterior-detail",
    priceFrom: 300,
    metaTitle: "Premium Interior + Exterior Detailing — Halifax & HRM",
    metaDescription:
      "Inside-out mobile detailing for Halifax, Dartmouth and Bedford. Full interior detail + foam-cannon exterior wash with DI rinse. Starting at $200. Book online.",
    keywords:
      "full car detailing halifax, interior exterior detail dartmouth, complete car detailing hrm, full detail nova scotia",
    hero: {
      eyebrow: "Premium Interior + Exterior",
      title: "Inside and out — done properly.",
      sub: "The complete daily-driver refresh. Full premium interior work plus a foam-cannon exterior wash with DI water rinse. Two and a half hours of hand detailing.",
    },
    sections: [
      {
        h: "The most-booked combo in HRM",
        p: "Our Premium Interior + Exterior package is what most Halifax customers pick for their spring or fall reset. It combines everything from our Express Interior with a proper foam-cannon exterior wash, so your whole vehicle — cabin, glass, wheels, and paint — is done in one appointment.",
      },
      {
        h: "Interior work",
        p: "Full hand detail of dash, vinyl, plastics, doors, jambs and trims. Vacuum of carpets, seats, mats and trunk. Interior windows cleaned. Shine applied to dashboard and doors.",
      },
      {
        h: "Exterior work",
        p: "Pre-rinse, foam cannon soap application, two-bucket microfiber pad hand wash, dedicated wheel and tire cleaning, and a deionized water rinse to prevent water spots. Wax is available as an add-on if you'd like extra protection and gloss.",
      },
    ],
    faqs: [
      {
        q: "Does this package include wax?",
        a: "Wax is not included by default — you can add Hand Wax (Carnauba) or Hand Wax + Clay Bar Decon as an add-on.",
      },
      {
        q: "Does it include shampooing seats and carpets?",
        a: "No — for shampoo extraction, choose the Premium Interior + Shampooing or the full Darkside Detail package.",
      },
      {
        q: "How long does it take?",
        a: "Approximately 2 hours 30 minutes for a sedan.",
      },
    ],
  },
  "signature-wash-wax-interior": {
    slug: "signature-wash-wax-interior",
    priceFrom: 250,
    metaTitle: "Signature Wash, Wax & Interior Detail — Halifax NS",
    metaDescription:
      "Our signature 3-hour maintenance detail. Foam-cannon wash + wax + full interior in one appointment. Halifax mobile service starting at $280. Book online.",
    keywords:
      "signature detail halifax, wash wax interior dartmouth, maintenance detail hrm, mobile detail package halifax",
    hero: {
      eyebrow: "Signature Wash, Wax & Interior",
      title: "The complete maintenance detail.",
      sub: "Three hours of hand work designed to refresh and protect your vehicle inside and out — foam-cannon exterior with your choice of wax, plus a full interior detail. All in one visit to your driveway.",
    },
    sections: [
      {
        h: "The perfect balance of protection and refresh",
        p: "The Signature Wash, Wax & Interior sits right in the sweet spot for most HRM drivers. You get real paint protection (foam cannon + wax) AND a fully detailed interior in a single 3-hour appointment. It's the package we recommend as an every-2-months maintenance detail for people who care about how their vehicle looks and holds up.",
      },
      {
        h: "Exterior work",
        p: "Pre-rinse to knock loose contaminants off. Foam cannon application with pH-balanced soap. Safe microfiber hand wash. Rims and tires cleaned dedicated. Deionized water rinse to prevent water spots on your paint. Then your choice of spray wax (long-lasting, easy) or hard carnauba wax (deeper wet-look shine).",
      },
      {
        h: "Interior work",
        p: "Every surface hand-detailed: dash, vinyl, plastics, doors, trims and jambs. Full vacuum of carpets, seats, mats and trunk. Interior windows finished streak-free. Final protective shine applied to dash and door panels.",
      },
      {
        h: "Who books this",
        p: "Halifax drivers who want the full monthly-maintenance treatment: interior + exterior + protection, all done properly, in one appointment. Skip the wax add-on hassle — it's included in this package.",
      },
    ],
    faqs: [
      {
        q: "How is this different from Premium Interior + Exterior?",
        a: "The main difference is wax. Premium Interior + Exterior does not include wax by default — you'd need to add it as an add-on. Signature bakes wax right in, plus we spend a little more time on both sides for a more complete result.",
      },
      {
        q: "Does this include shampooing seats and carpets?",
        a: "No — for shampoo extraction of fabric seats and carpets, choose the Premium Interior + Shampoo or the full Darkside Detail.",
      },
      {
        q: "How long does it take?",
        a: "About 3 hours of hand work for a standard sedan. Larger SUVs and trucks add 20–40 minutes.",
      },
      {
        q: "Spray wax or hard wax — which should I pick?",
        a: "Spray wax is quicker to apply and lasts about 2 months. Hard carnauba wax takes longer but adds noticeable wet-look depth and lasts 3–4 months. We'll ask on the day.",
      },
    ],
  },
  "premium-interior-shampoo": {
    slug: "premium-interior-shampoo",
    priceFrom: 250,
    metaTitle: "Premium Interior + Car Shampoo & Steam Cleaning — Halifax NS",
    metaDescription:
      "Deep car shampoo and steam extraction for Halifax, Dartmouth and HRM. Rescue coffee spills, pet accidents, salt stains and mystery smells. Starting at $230. Mobile service.",
    keywords:
      "car shampooing halifax, seat shampoo dartmouth, steam cleaning car interior hrm, car stain removal nova scotia, pet accident car cleaning",
    hero: {
      eyebrow: "Premium Interior + Shampoo",
      title: "Deep restoration for lived-in interiors.",
      sub: "Everything in our Premium Interior plus hot-water extraction shampooing (or steam) of your mats, floors and seats. Perfect for pet owners, growing families and hardworking trucks.",
    },
    sections: [
      {
        h: "The rescue package for Halifax families",
        p: "Kids, dogs, hockey gear, work boots, spilled coffee, salt from Barrington Street sidewalks in February — your interior takes a beating. This package brings it back with dedicated shampoo extraction on carpets, floor mats, seats and any fabric surfaces that need it.",
      },
      {
        h: "Best-effort stain lift",
        p: "We use professional-grade shampoo, hot water and mechanical agitation with an extractor. Most surface stains, salt lines and organic spills come out. However, older set-in stains, dye transfer, and damage from bleach or nail polish remover can't always be fully lifted — we'll always tell you what's realistic before we start.",
      },
      {
        h: "Steam option available",
        p: "For leather-heavy vehicles, tighter fabrics, or interiors where you want minimum moisture, we can steam-clean instead of wet extraction. Just mention it when you book.",
      },
    ],
    faqs: [
      {
        q: "Will the seats be wet after shampooing?",
        a: "Slightly damp — usually dry to the touch within 4–6 hours on a warm HRM day, faster with the windows cracked.",
      },
      {
        q: "Can you get rid of dog smell?",
        a: "Yes — combined with our Odour Eliminator add-on, our shampoo package removes almost all pet odours from most vehicles.",
      },
      {
        q: "Does this include the exterior?",
        a: "No, interior only. For interior + shampoo + exterior wash, book the full Darkside Detail.",
      },
    ],
  },
  "darkside-detail": {
    slug: "darkside-detail",
    priceFrom: 400,
    metaTitle: "Darkside Full Detail — Halifax & Dartmouth's Complete Package",
    metaDescription:
      "Our signature full detail: interior + shampooing + exterior wash. Three hours of hand work by Halifax's top-rated mobile detailer. Starting at $280 for sedans. HRM-wide service.",
    keywords:
      "full car detail halifax, complete detailing dartmouth, best detailing hrm, mobile full detail nova scotia, top detailer halifax",
    hero: {
      eyebrow: "Darkside Detail",
      title: "The full experience. Our namesake package.",
      sub: "Interior + shampoo + exterior. Three hours of hand work restoring your vehicle to a condition that turns heads on Spring Garden Road.",
    },
    sections: [
      {
        h: "Everything, inside and out",
        p: "The Darkside Detail is what we're known for across HRM. Every surface is touched by hand. It combines our Premium Interior work with hot-water shampoo extraction of seats, floors and mats, and pairs that with a proper foam-cannon exterior wash finished with a DI water rinse.",
      },
      {
        h: "Who books this",
        p: "This is the package our Halifax customers choose when they're selling a vehicle, welcoming a new one home, prepping for a road trip to Cape Breton, or just want the annual full reset. It's also our most-requested corporate/fleet appointment.",
      },
      {
        h: "Optional protection add-ons",
        p: "Add Hand Wax (Carnauba) or Hand Wax + Clay Bar Decon for extra gloss and paint protection. RainX for windshield water beading. Undercarriage cleaning if you drive back roads or beach access lanes.",
      },
    ],
    faqs: [
      {
        q: "How long does the full Darkside Detail take?",
        a: "About 3 hours of hand work for a standard sedan. Larger vehicles add 30–60 minutes.",
      },
      {
        q: "Can I book this back-to-back with another vehicle?",
        a: "Yes — our booking system supports up to 3 vehicles back-to-back on the same day.",
      },
      {
        q: "Do you offer this in winter?",
        a: "Our mobile season runs April through November. December–March is our off-season while HRM roads are unsafe for water-based mobile work.",
      },
    ],
  },
};

// -----------------------------------------------------------------------------
// LOCATION PAGES  —  HRM communities
// -----------------------------------------------------------------------------
export const LOCATION_PAGES = [
  {
    slug: "halifax",
    name: "Halifax",
    lat: 44.6488,
    lng: -63.5752,
    metaTitle: "Mobile Car Detailing in Halifax, Nova Scotia",
    metaDescription:
      "Top-rated mobile auto detailing in Halifax NS. We bring water, power and premium detailing tools to your driveway from downtown to the North End, Clayton Park and Spryfield. Starting at $150.",
    keywords: "car detailing halifax, mobile detailing halifax ns, auto detailing halifax nova scotia, best detailer halifax",
    intro:
      "We serve every neighbourhood in the city of Halifax — from the peninsula (South End, North End, downtown) out to Clayton Park, Fairview, Spryfield and Herring Cove. Whether you're on a driveway in Armdale, a condo lot on Barrington, or a rowhouse on Agricola, our mobile unit rolls up self-contained and gets to work.",
    neighborhoods: ["Downtown / Peninsula", "North End", "South End", "Clayton Park", "Fairview", "Spryfield", "Armdale", "Herring Cove"],
    local:
      "Halifax vehicles face a lot: harbour salt spray, brutal freeze-thaw cycles, road brine on Windsor Street, and pollen in spring from every tree along the Public Gardens. That's why every service we do in Halifax finishes with a deionized water rinse to prevent water spots and a hand microfiber dry — no drive-through swirl marks.",
  },
  {
    slug: "dartmouth",
    name: "Dartmouth",
    lat: 44.6717,
    lng: -63.5772,
    metaTitle: "Mobile Auto Detailing in Dartmouth, NS — Home of Darkside",
    metaDescription:
      "Dartmouth's own mobile detailing service. We're based right here and offer full interior, exterior, wash, wax and shampoo services from Woodside to Burnside and Cole Harbour. Book online.",
    keywords: "car detailing dartmouth, mobile detailing dartmouth ns, auto detailing dartmouth nova scotia, dartmouth detailer",
    intro:
      "Dartmouth is home base for Darkside Detailing. We serve every part of the city — Woodside, Portland Estates, Portland Hills, Downtown Dartmouth, Burnside, Lake Banook, and Cole Harbour. Because we're local, we can often accommodate same-week bookings and quick add-ons in Dartmouth faster than anywhere else in HRM.",
    neighborhoods: ["Downtown Dartmouth", "Woodside", "Portland Estates", "Portland Hills", "Burnside", "Lake Banook", "Manor Park"],
    local:
      "Dartmouth drivers get hit especially hard by winter road treatment along Portland Street and the Circumferential. Salt eats through clear coat and shortens the life of your paint. Our exterior packages remove that residue thoroughly — foam cannon, hand wash, deionized rinse — and our undercarriage cleaning add-on is a smart annual investment.",
  },
  {
    slug: "bedford",
    name: "Bedford",
    lat: 44.7286,
    lng: -63.6614,
    metaTitle: "Mobile Car Detailing in Bedford, Nova Scotia",
    metaDescription:
      "Premium mobile auto detailing in Bedford NS. We come to your driveway in Bedford South, Basinview, Sunnyside and along the Bedford Highway. Starting at $150.",
    keywords: "car detailing bedford, mobile detailing bedford ns, bedford car wash, auto detailing bedford nova scotia",
    intro:
      "Bedford is one of our most-requested service zones. From Bedford South and Basinview up to Kingswood and along the Bedford Highway, we bring the shop to your driveway. No dropping off, no waiting around a service bay — we work, you keep your day.",
    neighborhoods: ["Bedford South", "Basinview", "Sunnyside", "Bedford Highway corridor", "West Bedford", "Kingswood"],
    local:
      "Bedford's mix of suburban commutes and Bedford Highway grit is hard on paint. Our Wash & Wax and Premium Interior + Exterior packages are the two most-booked options here — quick, thorough, and easy to schedule around family life.",
  },
  {
    slug: "sackville",
    name: "Sackville",
    lat: 44.7719,
    lng: -63.6789,
    metaTitle: "Mobile Auto Detailing in Sackville & Lower Sackville, NS",
    metaDescription:
      "Mobile car detailing serving Lower Sackville, Middle Sackville and Beaver Bank. Interior, exterior, shampoo and wax packages starting at $150. We come to you.",
    keywords: "car detailing sackville, mobile detailing lower sackville, auto detailing beaver bank, sackville detailer nova scotia",
    intro:
      "We serve Lower Sackville, Middle Sackville, Upper Sackville and Beaver Bank. Our mobile unit handles the drive out from Dartmouth — you just need a driveway or laneway with about 15 feet of clear space.",
    neighborhoods: ["Lower Sackville", "Middle Sackville", "Beaver Bank", "Hammonds Plains-adjacent"],
    local:
      "Sackville drivers rack up serious highway kilometres on the 101 and Cobequid Pass. That means bug splatter, salt residue and constant road film — all of which our foam-cannon wash and DI water rinse are designed to strip cleanly without scratching the clearcoat.",
  },
  {
    slug: "cole-harbour",
    name: "Cole Harbour",
    lat: 44.6689,
    lng: -63.4664,
    metaTitle: "Mobile Car Detailing in Cole Harbour & Eastern Passage, NS",
    metaDescription:
      "Mobile auto detailing for Cole Harbour, Eastern Passage, Westphal and Forest Hills. Full interior, shampoo and exterior packages. Starting at $150.",
    keywords: "car detailing cole harbour, mobile detailing eastern passage, auto detailing westphal, cole harbour detailer",
    intro:
      "Cole Harbour, Westphal, Forest Hills, Eastern Passage — we cover all of it. Whether you're near the Cole Harbour Place, along Cow Bay Road, or over in Fisherman's Cove, we come to you fully equipped.",
    neighborhoods: ["Cole Harbour", "Westphal", "Forest Hills", "Eastern Passage", "Cow Bay", "Fisherman's Cove"],
    local:
      "Coastal Cole Harbour and Eastern Passage vehicles get extra salt spray from proximity to the water. Extra care goes into wheel wells, jambs and undercarriage areas during our detail work here.",
  },
  {
    slug: "clayton-park",
    name: "Clayton Park",
    lat: 44.6535,
    lng: -63.6478,
    metaTitle: "Mobile Auto Detailing in Clayton Park & Fairview, NS",
    metaDescription:
      "Mobile car detailing serving Clayton Park, Fairview and Rockingham. Interior, exterior and full packages. We come to your condo, townhouse or driveway.",
    keywords: "car detailing clayton park, mobile detailing fairview, rockingham car detailer, clayton park auto detailing",
    intro:
      "We serve Clayton Park, Clayton Park West, Fairview and Rockingham. Condos, townhouses and single-family homes — as long as we have driveway or lot access, we're set up.",
    neighborhoods: ["Clayton Park", "Clayton Park West", "Fairview", "Rockingham"],
    local:
      "Clayton Park's density means a lot of underground parking and condo lots. Our mobile unit is compact enough to work in most surface lots — just check with your building manager first if you're in a controlled-access lot.",
  },
  {
    slug: "fall-river",
    name: "Fall River",
    lat: 44.8073,
    lng: -63.6289,
    metaTitle: "Mobile Car Detailing in Fall River & Waverley, NS",
    metaDescription:
      "Premium mobile auto detailing serving Fall River, Waverley, Wellington and Windsor Junction. We come to your driveway with full interior + exterior packages.",
    keywords: "car detailing fall river, mobile detailing waverley, windsor junction detailer, fall river auto detailing nova scotia",
    intro:
      "We travel out to Fall River, Waverley, Wellington, Windsor Junction and Grand Lake regularly. It's a slightly longer drive from Dartmouth, but Fall River is one of our most-requested zones — lots of driveways, lots of family SUVs.",
    neighborhoods: ["Fall River", "Waverley", "Wellington", "Windsor Junction", "Grand Lake", "Oakfield"],
    local:
      "Fall River and Waverley drivers put serious kilometres on the Fall River Highway and Highway 118. Bug splatter, road film, and sap from all the surrounding trees are the three biggest paint enemies here. Our wash + wax and full-detail packages are the smart annual purchase for a well-loved family vehicle in this area.",
  },
  {
    slug: "eastern-passage",
    name: "Eastern Passage",
    lat: 44.5989,
    lng: -63.4736,
    metaTitle: "Mobile Auto Detailing in Eastern Passage & Cow Bay, NS",
    metaDescription:
      "Mobile car detailing serving Eastern Passage, Cow Bay, and Fisherman's Cove. Full interior + exterior packages for coastal HRM vehicles. Book online.",
    keywords: "car detailing eastern passage, mobile detailing cow bay, fisherman's cove car wash, eastern passage detailer nova scotia",
    intro:
      "Eastern Passage, Cow Bay, and Fisherman's Cove — some of the most beautiful coastline in HRM and some of the toughest conditions on your paint. We come out weekly and bring full self-contained water + power.",
    neighborhoods: ["Eastern Passage", "Cow Bay", "Fisherman's Cove", "Hartlen Point", "Rainbow Haven"],
    local:
      "Living by the ocean is stunning but salt spray, sand, and coastal winds accelerate paint corrosion 2-3× faster than inland driving. Our exterior packages here always include a thorough undercarriage wash to strip salt from the parts most drivers never see.",
  },
  {
    slug: "herring-cove",
    name: "Herring Cove",
    lat: 44.5798,
    lng: -63.5793,
    metaTitle: "Mobile Car Detailing in Herring Cove & Spryfield, NS",
    metaDescription:
      "Mobile auto detailing in Herring Cove, Spryfield, and Sambro. We bring water, power and every tool for a full detail directly to your driveway on the South Shore.",
    keywords: "car detailing herring cove, mobile detailing spryfield, sambro car wash, herring cove detailer nova scotia",
    intro:
      "We service Herring Cove, Spryfield, Sambro, Ketch Harbour, and Portuguese Cove — the whole South Shore corridor within HRM. Coastal residents get priority on salt-removal season passes.",
    neighborhoods: ["Herring Cove", "Spryfield", "Sambro", "Ketch Harbour", "Portuguese Cove"],
    local:
      "The Herring Cove / Sambro loop is one of the most exposed coastlines in HRM. Salt, fog, and wind combined make every car here a rust magnet if left untreated. Our winter salt-removal service is the single best investment for local drivers.",
  },
  {
    slug: "timberlea",
    name: "Timberlea",
    lat: 44.6540,
    lng: -63.7448,
    metaTitle: "Mobile Auto Detailing in Timberlea, Beechville & Lakeside, NS",
    metaDescription:
      "Mobile car detailing serving Timberlea, Beechville, Lakeside, and Tantallon. Interior, exterior, and full packages. We come to your driveway across the St. Margarets Bay corridor.",
    keywords: "car detailing timberlea, mobile detailing beechville, lakeside car wash, tantallon detailer nova scotia",
    intro:
      "Timberlea, Beechville, Lakeside, Hubley and out to Tantallon — the St. Margarets Bay corridor is a regular route for us. Great family neighbourhoods, lots of driveways, easy access for our mobile unit.",
    neighborhoods: ["Timberlea", "Beechville", "Lakeside", "Hubley", "Tantallon"],
    local:
      "The commute from Timberlea and Tantallon into Halifax means a LOT of highway kilometres on the 103 and Highway 3. Bugs, gravel chips, and road film build up faster than most people realise. Our exterior packages with hand wax add-on are the most efficient way to protect that clearcoat between visits.",
  },
  {
    slug: "bedford-basin",
    name: "Bedford Basin",
    lat: 44.7013,
    lng: -63.6472,
    metaTitle: "Mobile Auto Detailing Around Bedford Basin — Rockingham to Bedford, NS",
    metaDescription:
      "Mobile car detailing covering the entire Bedford Basin loop — Rockingham, Clayton Park West, Larry Uteck, Bedford South. Book premium detailing to your driveway.",
    keywords: "car detailing bedford basin, mobile detailing larry uteck, rockingham detailer, bedford south car wash",
    intro:
      "The whole Bedford Basin ring — from Rockingham up through Larry Uteck, Bedford West, Bedford South and back down through Clayton Park West — is one of our fastest-growing service zones. Modern subdivisions, wide driveways, and lots of premium vehicles.",
    neighborhoods: ["Rockingham", "Larry Uteck", "Bedford West", "Bedford South", "Clayton Park West", "Prince's Lodge"],
    local:
      "The Bedford Highway corridor is one of the highest-traffic roads in HRM — that means heavy road film, brake dust, and salt in winter. Our Signature Wash + Wax + Interior is the go-to package for vehicles that live along the Basin.",
  },
];

// -----------------------------------------------------------------------------
// BLOG POSTS  —  long-tail SEO content
// -----------------------------------------------------------------------------
export const BLOG_POSTS = [
  {
    slug: "how-often-detail-car-nova-scotia",
    title: "How Often Should You Detail Your Car in Nova Scotia?",
    excerpt:
      "A season-by-season guide for Halifax and HRM drivers — from spring salt cleanup to fall pollen and pre-winter protection.",
    metaTitle: "How Often Should You Detail Your Car in Nova Scotia?",
    metaDescription:
      "How often to detail your car in Halifax and HRM. Season-by-season recommendations for wash, wax, interior and shampoo — from a Dartmouth mobile detailer.",
    keywords:
      "how often detail car halifax, car detailing schedule nova scotia, car wash frequency hrm",
    published: "2026-02-01",
    cover: "/services/darkside-detail.webp",
    body: [
      { h: null, p: "One of the most common questions we get from Halifax and Dartmouth drivers is: how often should I actually be detailing my car? The answer depends on how you drive, where you park, and the season. Here's how we recommend HRM drivers plan their detailing schedule." },
      { h: "Spring (April – May): the salt-off detail", p: "This is the most important detail of the year in Nova Scotia. Winter road salt, sand, and calcium chloride get everywhere — inside your carpets from boot slush, in your wheel wells, under your rockers, and baked onto your paint. A Premium Interior + Exterior or full Darkside Detail in April removes six months of accumulated damage before it becomes permanent." },
      { h: "Summer (June – August): maintenance rhythm", p: "During peak driving season, aim for a Wash & Wax every 4–6 weeks. Halifax pollen, coastal salt spray, and bug splatter accumulate fast. Regular waxing keeps the clearcoat protected and makes each wash easier because dirt has less to grab onto." },
      { h: "Fall (September – October): the deep interior", p: "Fall is the sweet spot for interior shampoo work. Your car has been used hard all summer — beach trips, hiking gear, campgrounds, kids in cleats. Book a Premium Interior + Shampoo or the full Darkside Detail before the leaves drop." },
      { h: "Pre-winter (November): protection layer", p: "Before December weather rolls in, a Hand Wax add-on (carnauba or paste) puts a protective barrier between your paint and the coming salt onslaught. This is arguably the highest-value 60 minutes of detailing you can add all year." },
      { h: "Bottom line", p: "For most Halifax drivers, we recommend one full detail annually (Darkside Detail), plus maintenance wash & wax visits every 4–6 weeks during our April–November season. Book online at darksidedetailing.ca and we come to your driveway." },
    ],
  },
  {
    slug: "winter-salt-damage-halifax",
    title: "Winter Salt Damage: Protecting Your Car in HRM",
    excerpt:
      "Nova Scotia winters are hard on vehicles. Here's exactly what road salt does to your car — and how to fight back come spring.",
    metaTitle: "Winter Salt Damage on Halifax Cars — What It Does & How to Fix It",
    metaDescription:
      "How road salt damages your car in Halifax winters and what to do about it. A practical spring detailing guide for HRM drivers from Darkside Detailing.",
    keywords:
      "winter salt damage car halifax, salt damage repair nova scotia, spring detailing hrm, road salt car protection",
    published: "2026-02-10",
    cover: "/gallery/rear-mud-after.webp",
    body: [
      { h: null, p: "If you drive in HRM through the winter, your vehicle is being attacked chemically for four straight months. Understanding exactly what's happening helps you know when — and why — to book detailing work." },
      { h: "What road salt actually does", p: "The rock salt (sodium chloride) and brine solutions used on Halifax streets are extremely corrosive to steel and aluminum. When salt gets wet — from slush, freezing rain, or a temperature swing — it produces an ionic solution that accelerates rust on any exposed metal, particularly your brake lines, exhaust system, rocker panels, and undercarriage." },
      { h: "The interior side", p: "Salt gets tracked in on boots and stays in the carpet fibres. It attracts moisture, creates that white salt-line stain on your mats and lower door panels, and slowly damages the fabric. Left untreated, it's very hard to fully remove." },
      { h: "The spring reset", p: "As soon as HRM plows finish their last run and daytime temps stay above zero, book a full detail. What you want:" },
      { h: "1. Exterior with DI water rinse", p: "Foam cannon + hand wash + DI water rinse pulls salt off the paint and clearcoat without adding scratches. Skip the drive-through — those recycled brushes drag grit across your paint." },
      { h: "2. Undercarriage cleaning", p: "Available as an add-on ($20). Flushes accumulated salt out of wheel wells, rocker panels, and frame areas." },
      { h: "3. Interior shampoo", p: "For carpet, floor mats, and lower seat cushions where salt has been ground in." },
      { h: "4. Wax protection", p: "Once everything is clean, a hand wax adds a barrier for the coming season." },
      { h: "Book your spring detail early", p: "April fills up fast every year — usually two weeks in advance. Book online at darksidedetailing.ca to grab a slot before the rush." },
    ],
  },
  {
    slug: "hand-wax-halifax-guide",
    title: "Why Hand Wax Still Wins for Halifax Drivers",
    excerpt:
      "The simplest, most consistent paint protection for HRM weather — no gimmicks, no overpriced add-ons.",
    metaTitle: "Hand Wax for Halifax Cars — What Actually Protects Your Paint",
    metaDescription:
      "Why regular hand wax is the smartest paint protection for Halifax and HRM drivers. Real-world advice from a Dartmouth mobile detailer.",
    keywords:
      "hand wax halifax, carnauba wax dartmouth, paint protection nova scotia, car wax hrm",
    published: "2026-02-15",
    cover: "/services/wash-wax.webp",
    body: [
      { h: null, p: "Halifax winters, salt, sun, sea air — paint takes a beating out here. Here's the honest answer on what actually protects it for a daily driver." },
      { h: "What wax actually does", p: "Wax is a sacrificial layer that sits on top of your clearcoat. Carnauba (natural) or synthetic — either way, it fills in tiny surface imperfections, adds warmth and depth to the paint, and provides hydrophobic protection so water beads and dirt slides off easier. It lasts roughly 2–4 months in HRM weather." },
      { h: "Why we recommend wax over gimmicks", p: "Wax is inexpensive, easy to reapply, and doesn't lock imperfections into your clearcoat. For a daily driver in Halifax weather, that flexibility matters more than any long-term promise." },
      { h: "Our recommendation for most Halifax drivers", p: "Regular hand wax (carnauba or paste) applied every 2–3 months. Add clay bar decontamination twice a year. This is what we build into our Wash & Wax and Darkside Detail packages — dialed in for Nova Scotia conditions." },
      { h: "Book your next wax", p: "Book online at darksidedetailing.ca. We come to you across Halifax, Dartmouth, Bedford, Sackville, and the surrounding HRM communities." },
    ],
  },
  {
    slug: "pet-hair-removal-tips",
    title: "Getting Pet Hair Out of Your Car (For Real This Time)",
    excerpt:
      "If you've owned a Lab, Husky or Golden in HRM, you know. Here's how professionals actually remove pet hair.",
    metaTitle: "Pet Hair Removal for Halifax Dog Owners — Detailer Tips",
    metaDescription:
      "How to actually remove pet hair from your car interior. Tips from a Halifax mobile detailer who deals with Lab, Husky and Golden hair every week.",
    keywords:
      "pet hair removal car halifax, dog hair car cleaning dartmouth, remove pet hair car seats hrm",
    published: "2026-02-20",
    cover: "/gallery/rear-seats-after.webp",
    body: [
      { h: null, p: "Halifax is a dog city. Point Pleasant Park, McNabs Island, Long Lake — everyone brings their dog, and every dog leaves half their coat in your back seat. Here's what actually works to remove pet hair, based on doing this professionally every week." },
      { h: "Why regular vacuuming isn't enough", p: "Standard vacuums pull loose surface hair. But dog hair — especially from double-coated breeds like Huskies, Labs and Goldens — weaves itself into the fibres of your seats and carpet. A vacuum alone barely touches it." },
      { h: "What professionals do", p: "Three things: mechanical agitation (rubber pet-hair brushes or specialized rakes that lift embedded hair from fabric), followed by high-powered extraction vacuuming, followed by a compressed-air blowout to reach hair trapped in seat seams and under trim panels. All three steps, in that order." },
      { h: "DIY options for HRM dog owners", p: "Between professional details, keep two things in the car: a rubber-bristle brush (cheap at any Halifax auto supply store) and a lint roller. Brush the seats one direction to pile up the hair, then vacuum. Lint rollers finish the job on cloth headliners and door cards." },
      { h: "Book the add-on", p: "Our Pet Hair Removal add-on ($30 sedan / $40 SUV / $50 larger SUV) is included with Interior packages and does the full three-step professional process. Add it when you book online." },
    ],
  },
];
